'use client';
import { z } from 'zod';

export const listingSchema = z.object({
  id: z.string(),
  vin: z.string(),
  listingId: z.string(),
  title: z.string(),
  subTitle: z.string(),
  url: z.string(),
  make: z.string(),
  model: z.string(),
  countryCode: z.string(),
  stateCode: z.string(),
  countryName: z.string(),
  stateName: z.string(),
  zipCode: z.number(),
  year: z.number(),
  vehicleEraId: z.string(),
  price: z.number(),
  orBestOffer: z.boolean(),
  videoUrl1: z.string(),
  videoUrl2: z.string(),
  videoUrl3: z.string(),
  mainImageId: z.string(),
  mainImageUrl: z.string(),
  titleStatus: z.string(),
  transmission: z.string(),
  odometer: z.number(),
  engineSize: z.string(),
  engineType: z.string(),
  enginePower: z.string(),
  vehicleRestorationHistory: z.string(),
  vehicleRestorationHistoryExplained: z.string(),
  exteriorColor: z.string(),
  interiorColor: z.string(),
  exteriorCondition: z.string(),
  exteriorConditionMoreDescription: z.string(),
  seatMaterial: z.string(),
  airConditioning: z.boolean(),
  bucketSeats: z.boolean(),
  powerBrakes: z.boolean(),
  powerSteering: z.boolean(),
  powerWindows: z.boolean(),
  powerSeats: z.boolean(),
  tiltWheel: z.boolean(),
  hasConvertibleTop: z.boolean(),
  shortVehicleDescription: z.string(),
  engineCondition: z.string(),
  engineHistory: z.string(),
  carImages: z.array(
    z.object({
      imageId: z.string(),
      imageUrl: z.string(),
      isMain: z.boolean(),
    }),
  ),
  titleStatusName: z.string(),
  transmissionName: z.string(),
  engineSizeName: z.string(),
  engineTypeName: z.string(),
  enginePowerName: z.string(),
  vehicleRestorationHistoryName: z.string(),
  status: z.string(),
});

export type ListingFormValues = z.infer<typeof listingSchema>;

export const extendedVehicleInfoSchema = z.object({
  exteriorColor: z.string().optional(),
  exteriorCondition: z.string().optional(),
  exteriorConditionMoreDescription: z.string().optional(),
  seatMaterial: z.string().optional(),
  seatMaterialOther: z.string().optional(),
  interiorColor: z.string().optional(),
});

export const comfortFeaturesSchema = z.object({
  airConditioning: z.boolean(),
  bucketSeats: z.boolean(),
  powerBrakes: z.boolean(),
  powerSteering: z.boolean(),
  powerWindows: z.boolean(),
  powerSeats: z.boolean(),
  tiltWheel: z.boolean(),
  hasConvertibleTop: z.boolean(),
});

export type ComfortFeaturesFormValues = z.infer<typeof comfortFeaturesSchema>;

export const vehicleInfoSchema = z.object({
  vin: z.string().min(1, 'VIN is required'),
  make: z.string().min(1, 'Make is required'),
  model: z.string().min(1, 'Model is required'),
  price: z.preprocess(
    (val) => {
      if (val === '' || val === undefined || val === null) return undefined;
      // Strip non-digits so formatted values like "26,500" become 26500
      const numeric = String(val).replace(/[^\d]/g, '');
      if (numeric === '') return undefined;

      const num = Number(numeric);
      return Number.isNaN(num) ? undefined : Math.round(num);
    },
    z
      .number({ invalid_type_error: 'Price is not valid', required_error: 'Price is required' })
      .int('Price must be a whole number')
      .positive('Price must be positive')
      .max(999999, 'Price must not exceed 999999'),
  ) as z.ZodType<number | string>,
  orBestOffer: z.boolean().default(false) as z.ZodType<boolean>,
  year: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Year is not valid', required_error: 'Year is required' })
      .positive('Year must be positive'),
  ) as z.ZodType<number | string>,
  countryCode: z.string().min(1, 'Country is required'),
  countryName: z.string().optional(),
  stateCode: z.string().min(1, 'State is required'),
  stateName: z.string().optional(),
  zipCode: z.preprocess(
    (val) => {
      if (val === '' || val === undefined || val === null) return undefined;
      // Trim string values before converting to number
      const trimmedVal = typeof val === 'string' ? val.trim() : String(val).trim();
      return trimmedVal === '' ? undefined : Number(trimmedVal);
    },
    z
      .number({ invalid_type_error: 'Zip code is not valid', required_error: 'Zip code is required' })
      .positive('Zip code must be positive'),
  ) as z.ZodType<number | string>,
  city: z.string().min(1, 'City is required'),
  vehicleEra: z.string().min(1, 'Era is required'),
  vehicleEraName: z.string().optional(),
  shortVehicleDescription: z.string().min(1, 'Description is required').max(500, 'Max 500 characters'),
});

export const vehicleImagesSchema = z.object({
  images: z.array(z.instanceof(File)).min(1, 'At least one image is required'),
});

export type VehicleImagesFormValues = z.infer<typeof vehicleImagesSchema>;

const videoUrlRegex = /^(https?:\/\/(www\.)?(youtube\.com|youtu\.be|vimeo\.com)\/\S+)$/;

export const videoLinksSchema = z.object({
  videoUrl1: z
    .string()
    .regex(videoUrlRegex, 'There seems to be an issue with the link. Please double-check your video and try again.')
    .or(z.literal('')),
  videoUrl2: z
    .string()
    .regex(videoUrlRegex, 'There seems to be an issue with the link. Please double-check your video and try again.')
    .or(z.literal('')),
  videoUrl3: z
    .string()
    .regex(videoUrlRegex, 'There seems to be an issue with the link. Please double-check your video and try again.')
    .or(z.literal('')),
});

export type VideoLinksFormValues = z.infer<typeof videoLinksSchema>;

export const vehicleStatusSchema = z.object({
  titleStatus: z.string().optional(),
  odometer: z
    .preprocess(
      (val) => {
        if (val === '' || val === undefined || val === null) return undefined;
        const numeric = String(val).replace(/[^\d]/g, '');
        if (numeric === '') return undefined;

        return Number(numeric);
      },
      z.number({ invalid_type_error: 'Odometer is not valid' }),
    )
    .optional()
    .or(z.literal('')) as z.ZodType<number | string>,

  engineCondition: z.string().optional(),
  vehicleRestorationHistory: z.string().optional(),
  vehicleRestorationHistoryExplained: z.string().optional(),
  transmission: z.string().optional(),
  engineSize: z.string().optional(),
  engineHistory: z.string().optional(),
  restorationHistoryDesc: z.string().optional(),
});

export type VehicleStatusFormValues = z.infer<typeof vehicleStatusSchema>;
